/** @forge-header
 * @id          consolidatedMoneylines
 * @role        Consolidated moneyline table | single-row per game, dual-venue odds side-by-side
 * @pattern     component
 * @tier        T2
 * @purity      none
 * @units       cents
 * @money-path  yes
 * @fan-out     ~1
 * @upstream    nbaConsolidated/types:ConsolidatedGameBooks
 *              | sportsDiscovery:getSeriesTicker,Sport
 *              | hooks/useTableSort:useTableSort
 *              | sportsStream:SportsLoadingState
 *              | ./MoneylineGameRow:MoneylineGameRow
 *              | ./tradeTypes:TradeClickRequest
 *              | ./SportTableHeader:SportTableHeader
 *              | ./LoadingSkeletonRows:LoadingSkeletonRows
 *              | ./ShowMoreFooter:ShowMoreFooter
 * @downstream  ValueDashboardView
 * @exports     ConsolidatedMoneylines:component | TradeClickRequest:type (re-export)
 * @test        untested
 * @state       showAllRows:boolean | sort via useTableSort
 */

import { cn } from '@/lib/infra/utils';
import { Th } from '@/components/atoms/TableCells';
import { SortableTh } from '@/components/atoms/SortableTh';
import { memo, useMemo, useState } from 'react';
import { useTableSort } from '@/hooks/ui/useTableSort';
import type { ConsolidatedGameBooks } from '@/lib/nbaConsolidated/types';
import { getSeriesTicker, type Sport } from '@/lib/sportsDiscovery';
import type { OddsMode } from './DashboardHeader';
import type { SportsLoadingState } from '@/lib/sportsStream';
import { MoneylineGameRow } from './MoneylineGameRow';
import { SportTableHeader } from './SportTableHeader';
import { LoadingSkeletonRows } from './LoadingSkeletonRows';
import { ShowMoreFooter } from './ShowMoreFooter';

// Re-export TradeClickRequest for backwards compatibility
export type { TradeClickRequest } from './tradeTypes';
import type { TradeClickRequest } from './tradeTypes';

interface ConsolidatedMoneylinesProps {
  games: ConsolidatedGameBooks[];
  isLoading?: boolean;
  loadingState?: SportsLoadingState;
  oddsMode: OddsMode;
  maxLiquidity?: number;
  expandedGameKeys: Set<string>;
  onToggleExpand: (gameKey: string) => void;
  onTradeClick?: (req: TradeClickRequest) => void;
  /** Navigate to MarketDetailView (primary click). Shift+click still opens trade modal. */
  onNavigateToMarket?: (marketTicker: string) => void;
  /** Sport for team name lookup and title (default: nba) */
  sport?: Sport;
  /** Collapse the entire table body, showing only the header bar */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  /** Compact mode — reduces header padding */
  compact?: boolean;
  onToggleCompact?: () => void;
  className?: string;
}

const TABLE_COLUMNS = 17;

export const ConsolidatedMoneylines = memo(function ConsolidatedMoneylines({
  games,
  isLoading = false,
  loadingState,
  maxLiquidity = 100000,
  oddsMode,
  expandedGameKeys,
  onToggleExpand,
  onTradeClick,
  onNavigateToMarket,
  sport = 'nba',
  collapsed = false,
  onToggleCollapsed,
  compact = false,
  onToggleCompact,
  className,
}: ConsolidatedMoneylinesProps) {
  const [showAllRows, setShowAllRows] = useState(true);
  const maxRows = 10;

  const sortExtractors = useMemo(
    () => ({
      rotation: (game: ConsolidatedGameBooks) => game.awayRotation ?? null,
    }),
    []
  );

  const { sorted, sortKey, sortDir, handleSort } = useTableSort(games, sortExtractors, {
    defaultSortKey: 'time',
    defaultSortDir: 'asc',
  });

  const visibleGames = useMemo(() => {
    if (showAllRows) return sorted;
    return sorted.slice(0, maxRows);
  }, [sorted, showAllRows]);

  const seriesTicker = getSeriesTicker(sport, 'moneyline');

  return (
    <div className={cn('bg-card border-border rounded-lg border', className)}>
      {/* Header with sport-prefixed title and series ticker */}
      <SportTableHeader
        sport={sport}
        gameCount={games.length}
        seriesTicker={seriesTicker}
        collapsed={collapsed}
        onToggleCollapsed={onToggleCollapsed}
        compact={compact}
        onToggleCompact={onToggleCompact}
        isLoading={isLoading}
        loadingState={loadingState}
      />

      {/* Table + footer — hidden when collapsed */}
      {!collapsed && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-border border-b">
                <tr>
                  <SortableTh
                    sortKey="rotation"
                    activeSortKey={sortKey}
                    activeSortDir={sortDir}
                    onSort={handleSort}
                    className="w-16"
                  >
                    Roto
                  </SortableTh>
                  <SortableTh
                    sortKey="date"
                    activeSortKey={sortKey}
                    activeSortDir={sortDir}
                    onSort={handleSort}
                  >
                    Game Date
                  </SortableTh>
                  <SortableTh
                    sortKey="time"
                    activeSortKey={sortKey}
                    activeSortDir={sortDir}
                    onSort={handleSort}
                  >
                    Time PT
                  </SortableTh>
                  <SortableTh
                    sortKey="team"
                    activeSortKey={sortKey}
                    activeSortDir={sortDir}
                    onSort={handleSort}
                  >
                    Away Team
                  </SortableTh>
                  <Th>Home Team</Th>
                  <Th className="text-right">Away Kalshi</Th>
                  <Th className="text-right">Home Kalshi</Th>
                  <Th className="w-16 text-right">Away K Vol</Th>
                  <Th className="w-16 text-right">Home K Vol</Th>
                  <Th className="w-24">Away K Liq</Th>
                  <Th className="w-24">Home K Liq</Th>
                  <Th className="text-right">Away Poly</Th>
                  <Th className="text-right">Home Poly</Th>
                  <Th className="w-16 text-right">Away P Vol</Th>
                  <Th className="w-16 text-right">Home P Vol</Th>
                  <Th className="w-24">Away P Liq</Th>
                  <Th className="w-24">Home P Liq</Th>
                </tr>
              </thead>
              <tbody className="divide-border divide-y">
                {isLoading && games.length === 0 ? (
                  <LoadingSkeletonRows columns={TABLE_COLUMNS} />
                ) : (
                  visibleGames.map((game) => (
                    <MoneylineGameRow
                      key={game.key}
                      game={game}
                      oddsMode={oddsMode}
                      maxLiquidity={maxLiquidity}
                      expanded={expandedGameKeys.has(game.key)}
                      sport={sport}
                      onToggleExpand={onToggleExpand}
                      onTradeClick={onTradeClick}
                      onNavigateToMarket={onNavigateToMarket}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Expand / minimize (show more rows) */}
          {games.length > maxRows && (
            <ShowMoreFooter
              totalCount={games.length}
              visibleCount={Math.min(maxRows, games.length)}
              showAll={showAllRows}
              onToggle={() => setShowAllRows((v) => !v)}
            />
          )}

          {/* Empty state */}
          {games.length === 0 && !isLoading && (
            <div className="text-muted-foreground p-8 text-center">No games available</div>
          )}
        </>
      )}
    </div>
  );
});

export default ConsolidatedMoneylines;
